import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Check, ChevronRight, X, ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";

function MockupHeader({ kind, counter, source = "GPC", difficulty = "Muy Fácil" }: { kind: string; counter: string; source?: string; difficulty?: string }) {
  const sourceStyles = source === "PRONAM"
    ? "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-900/20 dark:text-violet-300"
    : "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300";
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 sm:mb-4 sm:gap-2">
      <span className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground sm:text-[11px]">
        {kind}
      </span>
      <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${sourceStyles}`}>
        {source}
      </span>
      <span className="rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-300 sm:text-[11px]">
        {difficulty}
      </span>
      <span className="ml-auto text-[10px] font-medium text-muted-foreground sm:text-xs">{counter}</span>
    </div>
  );
}

const mockups = [
  {
    type: "Flashcard clásica",
    content: (
      <div className="flex h-full flex-col">
        <MockupHeader kind="Clásica" counter="1 / 20" source="GPC" difficulty="Muy Fácil" />
        <div className="flex flex-1 flex-col overflow-hidden rounded-xl border-2 border-border bg-card shadow-sm sm:rounded-2xl">
          <div className="flex flex-1 flex-col p-4 sm:p-6">
            <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-muted-foreground sm:gap-1.5 sm:text-[10px]">
              <Brain className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              Pregunta
            </div>
            <div className="flex flex-1 items-center justify-center py-2 sm:py-4">
              <p className="mx-auto max-w-xl text-center text-sm font-semibold leading-relaxed text-foreground sm:text-lg md:text-xl">
                ¿Cuál es el estudio de elección para confirmar el diagnóstico de Hipertensión Arterial Sistémica según la GPC?
              </p>
            </div>
          </div>
          <div className="space-y-2 border-t border-border bg-muted/30 p-3 sm:space-y-3 sm:p-4">
            <Button
              variant="outline"
              className="h-10 w-full rounded-lg border-2 border-primary/30 text-sm font-bold text-primary hover:border-primary/50 hover:bg-primary/5 sm:h-12 sm:text-base"
            >
              Ver respuesta
            </Button>
            <div className="grid grid-cols-2 gap-2 opacity-60">
              <div className="flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50/50 px-2 py-1.5 text-[11px] font-semibold text-red-600 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-400 sm:text-xs">
                <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> No la sabía
              </div>
              <div className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50/50 px-2 py-1.5 text-[11px] font-semibold text-emerald-600 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-400 sm:text-xs">
                <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> La sabía
              </div>
            </div>
            <p className="text-center text-[10px] text-muted-foreground sm:text-[11px]">Espacio para revelar</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    type: "Flashcard Cloze",
    content: (
      <div className="flex h-full flex-col">
        <MockupHeader kind="Cloze" counter="2 / 20" source="GPC" difficulty="Fácil" />
        <div className="flex flex-1 flex-col overflow-hidden rounded-xl border-2 border-border bg-card shadow-sm sm:rounded-2xl">
          <div className="flex flex-1 flex-col items-center justify-center p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2 text-center text-sm font-medium leading-relaxed text-foreground sm:gap-y-3 sm:text-lg sm:leading-loose md:text-xl">
              <span>El diagnóstico de hipertensión arterial se establece con cifras ≥ a</span>
              <span className="mx-0.5 inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-mono text-xs font-bold text-emerald-700 shadow-sm dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 sm:px-3 sm:text-base">
                140/90
              </span>
              <span>mmHg en la</span>
              <span className="mx-0.5 inline-flex items-center rounded-md border-b-2 border-blue-400 bg-blue-50 px-2 py-0.5 font-mono text-xs font-bold tracking-widest text-blue-600 shadow-sm dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300 sm:px-3 sm:text-base">
                [...]
              </span>
              <span>médica.</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 border-t border-border bg-muted/30 p-3 sm:gap-3 sm:p-4">
            <Button
              variant="outline"
              className="h-10 rounded-lg border-2 border-red-200 text-sm font-bold text-red-600 hover:border-red-300 hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 sm:h-12 sm:text-base"
            >
              <ThumbsDown className="mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" /> No la sabía
            </Button>
            <Button
              variant="outline"
              className="h-10 rounded-lg border-2 border-emerald-200 text-sm font-bold text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-400 sm:h-12 sm:text-base"
            >
              <ThumbsUp className="mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" /> La sabía
            </Button>
          </div>
        </div>
      </div>
    ),
  },
  {
    type: "Pregunta MCQ",
    content: (
      <div className="flex h-full flex-col">
        <MockupHeader kind="Simulador" counter="3 / 20" source="PRONAM" difficulty="Examen" />
        <div className="flex flex-1 flex-col gap-3 overflow-hidden rounded-xl border-2 border-border bg-card p-3 shadow-sm sm:gap-4 sm:rounded-2xl sm:p-5">
          <p className="text-sm font-semibold leading-snug text-foreground sm:text-base md:text-lg">
            ¿Cuál es el tratamiento farmacológico de primera línea en pacientes con HAS sin comorbilidades según la GPC?
          </p>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            {[
              { text: "Tiazidas o IECA", state: "correct" as const },
              { text: "Beta bloqueadores", state: "neutral" as const },
              { text: "Calcioantagonistas", state: "wrong" as const },
              { text: "ARA II", state: "neutral" as const },
            ].map((opt, i) => {
              const letter = String.fromCharCode(65 + i);
              const styles =
                opt.state === "correct"
                  ? "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20"
                  : opt.state === "wrong"
                  ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20"
                  : "border-border bg-background";
              const circle =
                opt.state === "correct"
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : opt.state === "wrong"
                  ? "border-red-500 bg-red-500 text-white"
                  : "border-border bg-muted text-muted-foreground";
              return (
                <div
                  key={i}
                  className={`flex w-full items-center gap-2 rounded-lg border-2 p-2 text-left sm:gap-3 sm:rounded-xl sm:p-3 ${styles}`}
                >
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold sm:h-8 sm:w-8 sm:text-sm ${circle}`}>
                    {opt.state === "correct" ? <Check className="h-3 w-3 sm:h-4 sm:w-4" /> : opt.state === "wrong" ? <X className="h-3 w-3 sm:h-4 sm:w-4" /> : letter}
                  </span>
                  <span className="flex-1 text-xs font-medium text-foreground sm:text-sm">{opt.text}</span>
                  {opt.state === "wrong" && (
                    <span className="hidden text-[10px] font-semibold uppercase text-red-600 dark:text-red-400 sm:inline">Tu respuesta</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-auto flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs dark:border-emerald-900/50 dark:bg-emerald-900/20 sm:text-sm">
            <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span className="font-semibold text-emerald-700 dark:text-emerald-300">¡Correcta!</span>
            <span className="ml-auto text-[11px] font-medium text-emerald-700/80 underline-offset-2 hover:underline dark:text-emerald-300/80 sm:text-xs">
              Ver explicación
            </span>
          </div>
        </div>
      </div>
    ),
  },
];

export function ProductPreview() {
  const [currentMockup, setCurrentMockup] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMockup((prev) => (prev + 1) % mockups.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="mx-auto mt-12 w-full max-w-full overflow-hidden border border-border bg-card shadow-[0_24px_70px_-40px_rgba(15,23,42,0.35)] lg:max-w-6xl">
      <div className="flex h-11 items-center justify-between border-b border-border bg-muted/40 px-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </div>
        <span className="min-w-0 truncate px-3 text-xs font-medium text-muted-foreground">repasogpc.com/app/estudia</span>
        <span className="w-12" />
      </div>

      <div className="grid min-h-[390px] lg:grid-cols-[240px_1fr]">
        <aside className="hidden border-r border-border bg-muted/20 p-5 lg:block">
          <p className="mb-4 text-xs font-semibold uppercase text-muted-foreground">Tu biblioteca</p>
          {["Medicina interna", "Pediatría", "Ginecología", "Cirugía"].map((area, index) => (
            <div
              key={area}
              className={`mb-2 flex items-center justify-between rounded-md px-3 py-2.5 text-sm ${
                index === 0 ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground"
              }`}
            >
              <span>{area}</span>
              <ChevronRight className="h-4 w-4" />
            </div>
          ))}
        </aside>

        <div className="min-w-0 p-3 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-end sm:justify-between sm:gap-4 sm:pb-5">
            <div className="min-w-0">
              <p className="text-xs font-medium text-primary sm:text-sm">Medicina interna / Cardiología</p>
              <h2 className="mt-1 break-words text-lg font-bold sm:text-2xl lg:text-3xl">Hipertensión arterial sistémica</h2>
            </div>
            <div className="flex gap-2">
              <span className="rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 sm:px-2.5 sm:py-1 sm:text-xs">
                PRONAM
              </span>
              <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 sm:px-2.5 sm:py-1 sm:text-xs">
                GPC
              </span>
            </div>
          </div>

          <div className="min-w-0 pt-4 sm:pt-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMockup}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="flex min-h-[340px] flex-col sm:min-h-[380px]"
              >
                {mockups[currentMockup].content}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
